import { Monitor, Moon, Sun, type LucideIcon } from 'lucide-react';

import type { Theme } from '@/lib/hooks';
import { useTheme } from '@/lib/hooks';
import { Tooltip } from '@/lib/ui';
import { cn } from '@/lib/utils';

const OPTIONS: { value: Theme; label: string; Icon: LucideIcon }[] = [
  { value: 'light', label: 'Use light theme', Icon: Sun },
  { value: 'system', label: 'Use system theme', Icon: Monitor },
  { value: 'dark', label: 'Use dark theme', Icon: Moon },
] as const;

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className="inline-flex items-center gap-0.5 rounded-xl border border-[var(--color-glass-border)] bg-[var(--color-glass-bg)] p-1 shadow-sm backdrop-blur-[3px]"
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const selected = theme === value;
        return (
          <Tooltip key={value} content={label}>
            <button
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={label}
              onClick={() => setTheme(value)}
              className={cn(
                'flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-glass-bg)]',
                selected
                  ? 'border border-[var(--color-glass-border)] bg-[var(--color-glass-bg)] text-foreground shadow-sm backdrop-blur-[3px]'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="size-4" strokeWidth={1.75} aria-hidden />
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}
